import { ColorScale } from '@/theme/colors';
import moment from 'moment';

import {
  BaseDatasetDefinition,
  GRPCStatusCode,
  HTTPStatus,
  HTTPStatusCode,
  PERCENTILE_KEY,
  RelativeTimeRange,
  STATUS_KEY,
  TimeRange,
  WebsocketStatusCode,
} from './types';

export const ONE_DAY_IN_SECONDS = 60 * 60 * 24;

export function getStepSecondsForTimeRange([start, end]: TimeRange): number {
  const diff = moment(end).diff(start, 'seconds');

  switch (true) {
    case diff <= 60 * 10:
      return 30;
    case diff <= 60 * 60:
      return 30;
    case diff <= 60 * 60 * 6:
      return 60;
    case diff <= ONE_DAY_IN_SECONDS:
      return 10 * 60;
    case diff <= ONE_DAY_IN_SECONDS * 3:
      return 60 * 60;
    case diff <= ONE_DAY_IN_SECONDS * 7:
      return 60 * 60;
    default:
      return ONE_DAY_IN_SECONDS;
  }
}

export const STEP_SECONDS_FOR_TIME_RANGE: Record<RelativeTimeRange, number> = {
  [RelativeTimeRange.LAST_10_MINUTES]: 30,
  [RelativeTimeRange.LAST_1_HOUR]: 30,
  [RelativeTimeRange.LAST_6_HOURS]: 60,
  [RelativeTimeRange.LAST_24_HOURS]: 10 * 60,
  [RelativeTimeRange.LAST_3_DAYS]: 30 * 60,
  [RelativeTimeRange.LAST_7_DAYS]: 24 * 3600,
};

/**
 * For metrics in the form of events / unit time, this is the time range (in seconds) we use in the unit
 */

export function getTimeDivisorUnitForTimeRange([start, end]: TimeRange): moment.unitOfTime.Base {
  const diff = moment(end).diff(start, 'seconds');

  switch (true) {
    case diff <= 60 * 10:
      return 'minute';
    case diff <= 60 * 60:
      return 'minute';
    case diff <= 60 * 60 * 6:
      return 'minute';
    case diff <= ONE_DAY_IN_SECONDS:
      return 'hour';
    case diff <= ONE_DAY_IN_SECONDS * 3:
      return 'hour';
    case diff <= ONE_DAY_IN_SECONDS * 7:
      return 'hour';
    default:
      return 'day';
  }
}

export function getShortTimeDivisorUnitForTimeRange([start, end]: TimeRange): string {
  const diff = moment(end).diff(start, 'seconds');

  switch (true) {
    case diff <= 60 * 10:
      return 'min';
    case diff <= 60 * 60:
      return 'min';
    case diff <= 60 * 60 * 6:
      return 'min';
    case diff <= ONE_DAY_IN_SECONDS:
      return 'hr';
    case diff <= ONE_DAY_IN_SECONDS * 3:
      return 'hr';
    case diff <= ONE_DAY_IN_SECONDS * 7:
      return 'hr';
    default:
      return 'day';
  }
}

export function getTimeDivisorSecondsForTimeRange([start, end]: TimeRange): number {
  return moment.duration(1, getTimeDivisorUnitForTimeRange([start, end])).asSeconds();
}

export const PERCENTILE_TO_COLOR: Record<PERCENTILE_KEY, BaseDatasetDefinition['color']> = {
  p50: 'info',
  p90: 'success',
  p95: 'warning',
  p99: 'error',
};

export const HTTP_STATUS_CODE_TO_COLOR: Record<HTTPStatusCode, BaseDatasetDefinition['color']> = {
  [HTTPStatusCode.OK]: ColorScale.green[500],
  [HTTPStatusCode.ACCEPTED]: ColorScale.green[600],
  [HTTPStatusCode.BAD_REQUEST]: ColorScale.yellow[500],
  [HTTPStatusCode.NOT_FOUND]: ColorScale.yellow[600],
  [HTTPStatusCode.UNPROCESSABLE_ENTITY]: ColorScale.yellow[700],
  [HTTPStatusCode.FAILED_DEPENDENCY]: ColorScale.yellow[800],
  [HTTPStatusCode.CLIENT_CLOSED_REQUEST]: ColorScale.yellow[400],
  [HTTPStatusCode.INTERNAL_SERVER_ERROR]: ColorScale.red[600],
  [HTTPStatusCode.BAD_GATEWAY]: ColorScale.red[700],
  [HTTPStatusCode.SERVICE_UNAVAILABLE]: ColorScale.red[500],
  [HTTPStatusCode.GATEWAY_TIMEOUT]: ColorScale.red[800],
};

export const HTTP_STATUS_CODE_TO_LABEL: Record<HTTPStatusCode, string> = {
  [HTTPStatusCode.OK]: 'OK',
  [HTTPStatusCode.ACCEPTED]: 'Accepted',
  [HTTPStatusCode.BAD_REQUEST]: 'Bad request',
  [HTTPStatusCode.NOT_FOUND]: 'Not found',
  [HTTPStatusCode.UNPROCESSABLE_ENTITY]: 'Unprocessable entity',
  [HTTPStatusCode.FAILED_DEPENDENCY]: 'Failed dependency',
  [HTTPStatusCode.CLIENT_CLOSED_REQUEST]: 'Client closed request',
  [HTTPStatusCode.INTERNAL_SERVER_ERROR]: 'Internal server error',
  [HTTPStatusCode.BAD_GATEWAY]: 'Bad gateway',
  [HTTPStatusCode.SERVICE_UNAVAILABLE]: 'Service unavailable',
  [HTTPStatusCode.GATEWAY_TIMEOUT]: 'Gateway timeout',
};

export const HTTP_STATUS_TO_COLOR: Record<STATUS_KEY, BaseDatasetDefinition['color']> = {
  [HTTPStatus.Status2xx]: 'success',
  [HTTPStatus.Status4xx]: 'warning',
  [HTTPStatus.Status5xx]: 'error',
};

export const HTTP_STATUS_TO_LABEL: Record<STATUS_KEY, string> = {
  [HTTPStatus.Status2xx]: '2XX',
  [HTTPStatus.Status4xx]: '4XX',
  [HTTPStatus.Status5xx]: '5XX',
};

export const HTTP_STATUS_TO_HTTP_STATUS_CODES: Record<HTTPStatus, HTTPStatusCode[]> = {
  [HTTPStatus.Status2xx]: [HTTPStatusCode.OK, HTTPStatusCode.ACCEPTED],
  [HTTPStatus.Status4xx]: [
    HTTPStatusCode.BAD_REQUEST,
    HTTPStatusCode.NOT_FOUND,
    HTTPStatusCode.UNPROCESSABLE_ENTITY,
    HTTPStatusCode.FAILED_DEPENDENCY,
    HTTPStatusCode.CLIENT_CLOSED_REQUEST,
  ],
  [HTTPStatus.Status5xx]: [
    HTTPStatusCode.INTERNAL_SERVER_ERROR,
    HTTPStatusCode.BAD_GATEWAY,
    HTTPStatusCode.SERVICE_UNAVAILABLE,
    HTTPStatusCode.GATEWAY_TIMEOUT,
  ],
};

export const GRPC_STATUS_TO_COLOR: Record<GRPCStatusCode, string> = {
  [GRPCStatusCode.OK]: ColorScale.green[500],
  [GRPCStatusCode.CANCELLED]: ColorScale.gray[500],
  [GRPCStatusCode.UNKNOWN]: ColorScale.gray[700],
  [GRPCStatusCode.INVALID_ARGUMENT]: ColorScale.yellow[500],
  [GRPCStatusCode.DEADLINE_EXCEEDED]: ColorScale.red[500],
  [GRPCStatusCode.NOT_FOUND]: ColorScale.yellow[700],
  [GRPCStatusCode.ALREADY_EXISTS]: ColorScale.yellow[400],
  [GRPCStatusCode.PERMISSION_DENIED]: ColorScale.yellow[600],
  [GRPCStatusCode.RESOURCE_EXHAUSTED]: ColorScale.purple[500],
  [GRPCStatusCode.FAILED_PRECONDITION]: ColorScale.purple[700],
  [GRPCStatusCode.ABORTED]: ColorScale.red[700],
  [GRPCStatusCode.OUT_OF_RANGE]: ColorScale.purple[400],
  [GRPCStatusCode.UNIMPLEMENTED]: ColorScale.red[400],
  [GRPCStatusCode.INTERNAL]: ColorScale.red[600],
  [GRPCStatusCode.UNAVAILABLE]: ColorScale.red[300],
  [GRPCStatusCode.DATA_LOSS]: ColorScale.red[800],
  [GRPCStatusCode.UNAUTHENTICATED]: ColorScale.yellow[300],
};

export const GRPC_STATUS_TO_LABEL: Record<GRPCStatusCode, string> = {
  [GRPCStatusCode.OK]: 'OK',
  [GRPCStatusCode.CANCELLED]: 'Cancelled',
  [GRPCStatusCode.UNKNOWN]: 'Unknown',
  [GRPCStatusCode.INVALID_ARGUMENT]: 'Invalid argument',
  [GRPCStatusCode.DEADLINE_EXCEEDED]: 'Deadline exceeded',
  [GRPCStatusCode.NOT_FOUND]: 'Not found',
  [GRPCStatusCode.ALREADY_EXISTS]: 'Already exists',
  [GRPCStatusCode.PERMISSION_DENIED]: 'Permission denied',
  [GRPCStatusCode.RESOURCE_EXHAUSTED]: 'Resource exhausted',
  [GRPCStatusCode.FAILED_PRECONDITION]: 'Failed precondition',
  [GRPCStatusCode.ABORTED]: 'Aborted',
  [GRPCStatusCode.OUT_OF_RANGE]: 'Out of range',
  [GRPCStatusCode.UNIMPLEMENTED]: 'Unimplemented',
  [GRPCStatusCode.INTERNAL]: 'Internal',
  [GRPCStatusCode.UNAVAILABLE]: 'Unavailable',
  [GRPCStatusCode.DATA_LOSS]: 'Data loss',
  [GRPCStatusCode.UNAUTHENTICATED]: 'Unauthenticated',
};

export const WEBSOCKET_STATUS_TO_COLOR: Record<WebsocketStatusCode, string> = {
  [WebsocketStatusCode.NORMAL_CLOSURE]: ColorScale.green[500],
  [WebsocketStatusCode.GOING_AWAY]: ColorScale.green[700],
  [WebsocketStatusCode.PROTOCOL_ERROR]: ColorScale.yellow[500],
  [WebsocketStatusCode.UNSUPPORTED_DATA]: ColorScale.yellow[700],
  [WebsocketStatusCode.NO_STATUS_RECEIVED]: ColorScale.gray[500],
  [WebsocketStatusCode.ABNORMAL_CLOSURE]: ColorScale.purple[500],
  [WebsocketStatusCode.INVALID_FRAME_PAYLOAD_DATA]: ColorScale.yellow[400],
  [WebsocketStatusCode.POLICY_VIOLATION]: ColorScale.yellow[600],
  [WebsocketStatusCode.MESSAGE_TOO_BIG]: ColorScale.yellow[300],
  [WebsocketStatusCode.MANDATORY_EXT]: ColorScale.yellow[800],
  [WebsocketStatusCode.INTERNAL_ERROR]: ColorScale.red[500],
  [WebsocketStatusCode.SERVICE_RESTART]: ColorScale.red[700],
  [WebsocketStatusCode.TRY_AGAIN_LATER]: ColorScale.red[400],
  [WebsocketStatusCode.BAD_GATEWAY]: ColorScale.red[600],
  [WebsocketStatusCode.TLS_HANDSHAKE]: ColorScale.red[300],
};

export const WEBSOCKET_STATUS_TO_LABEL: Record<WebsocketStatusCode, string> = {
  [WebsocketStatusCode.NORMAL_CLOSURE]: 'Normal closure',
  [WebsocketStatusCode.GOING_AWAY]: 'Going away',
  [WebsocketStatusCode.PROTOCOL_ERROR]: 'Protocol error',
  [WebsocketStatusCode.UNSUPPORTED_DATA]: 'Unsupported data',
  [WebsocketStatusCode.NO_STATUS_RECEIVED]: 'No status received',
  [WebsocketStatusCode.ABNORMAL_CLOSURE]: 'Abnormal closure',
  [WebsocketStatusCode.INVALID_FRAME_PAYLOAD_DATA]: 'Invalid frame payload data',
  [WebsocketStatusCode.POLICY_VIOLATION]: 'Policy violation',
  [WebsocketStatusCode.MESSAGE_TOO_BIG]: 'Message too big',
  [WebsocketStatusCode.MANDATORY_EXT]: 'Mandatory extension missing',
  [WebsocketStatusCode.INTERNAL_ERROR]: 'Internal error',
  [WebsocketStatusCode.SERVICE_RESTART]: 'Service restart',
  [WebsocketStatusCode.TRY_AGAIN_LATER]: 'Try again later',
  [WebsocketStatusCode.BAD_GATEWAY]: 'Bad gateway',
  [WebsocketStatusCode.TLS_HANDSHAKE]: 'TLS handshake failure',
};
